import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { useAtomValue, useAtom } from 'jotai';
import TuneIcon from '@mui/icons-material/Tune';
import MapIcon from '@mui/icons-material/Map';
import TableChartIcon from '@mui/icons-material/TableChart';
import Tooltip from '@mui/material/Tooltip';
import northPolygonData from '../../Components/Map/Geojsons/northPolygonData.geojson';
import centralAPolygonData from '../../Components/Map/Geojsons/centralAPolygonData.geojson';
import centralBPolygonData from '../../Components/Map/Geojsons/centralBPolygonData.geojson';
import kashmirPolygonData from '../../Components/Map/Geojsons/kashmirPolygonData.geojson';
import southPolygonData from '../../Components/Map/Geojsons/southPolygonData.geojson';
import completePakistanPolygonData from '../../Components/Map/Geojsons/completePakistanPolygonData.geojson';
import pakistanPolygonData from '../../Components/Map/Geojsons/pakistanPolygonData.geojson';
import { fetchInventoryDataByNetwork } from './functions/get_inventory.functions';
import { geoServerSites } from '../../common/functions/get_geoServer_sites.function';
import { geoServerLinks } from '../../common/functions/get_geoServer_links.function';

import { kmzNetwork, myFiber, leasedFiber, rowFiber } from '../../common/atom/atoms';
import PartnerAndNetwrokButtons from './partner_and_network_button.component';
import LeasedFiberButtonsComponent from './leasedFiber_buttons.component';
import LeasedFiberTable from './leasedFiber_table.component';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import LeasedFiberFilterDrawer from './LeasedFiberFilterDrawer';
import VendorLegend from './vendor_legend.component';
import { useBackdrop } from '../../common/components/backDrop_context.component';
import { boundaryLayer } from '../../common/functions/pak_boundry.function';
import { removeWmsLayer } from '../../common/functions/remove_wms_layer.function';
import { removeWmsSitesLayer } from '../../common/functions/remove_wms_site_layers.function';
import { handleRefresh } from '../../common/functions/refresh_map.function';
import { addWmsLayer } from './functions/add_wms_layer.function';
import RefreshMap from '../../common/components/refresh_map_component';
import apiClient from '../../common/functions/api_axios_instance.function';
import all_cities from '../../Components/Map/Geojsons/city_data.json';
import { findCityByAlias } from '../../common/functions/find_City_By_Alias.function';
import { findRegionByAlias } from '../../common/functions/find_Region_By_Alias.function';
import { isNewOtnImplementation } from '../../common/functions/otn_new_implementation.function';
mapboxgl.accessToken = process.env.REACT_APP_PUBLIC_MAP_BOX_GL_ACCESS_TOKEN;

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeInventoryItem = (item) => {
  const fiberPair = parseNumber(item.fiber_pair ?? item.pair_count);
  const usedPair = parseNumber(item.used_pair);
  const availablePairExplicit = parseNumber(item.available_pair ?? item.unused_pair);
  const computedAvailablePair =
    fiberPair !== undefined && usedPair !== undefined
      ? Math.max(fiberPair - usedPair, 0)
      : undefined;
  const commissionDate = item.commission_date ?? item.commissioned_at ?? null;
  const tenancyLabel = item.fiber_tenancy_type ?? item.tenancy_type ?? '';
  const normalizedTenancy = tenancyLabel ? String(tenancyLabel).toLowerCase().trim() : '';
  const isRowValue = item.is_row;
  const normalizedIsRow =
    typeof isRowValue === 'boolean'
      ? isRowValue
      : normalizedTenancy === 'row' ||
        (isRowValue !== undefined &&
          ['true', '1'].includes(String(isRowValue).toLowerCase().trim()));

  return {
    ...item,
    region: item.region ?? item.source_region ?? '',
    city: item.city ?? item.source_city ?? '',
    ring_unique_name: item.ring_unique_name ?? item.source_ring ?? '',
    source_region: item.source_region ?? item.region ?? '',
    source_city: item.source_city ?? item.city ?? '',
    source_ring: item.source_ring ?? item.ring_unique_name ?? '',
    trail_name: item.trail_name ?? '',
    fiber_pair: fiberPair ?? item.fiber_pair ?? item.pair_count ?? null,
    pair_count: fiberPair ?? item.pair_count ?? item.fiber_pair ?? null,
    used_pair: usedPair ?? item.used_pair ?? null,
    unused_pair: availablePairExplicit ?? computedAvailablePair ?? null,
    available_pair: computedAvailablePair ?? availablePairExplicit ?? null,
    commission_date: commissionDate,
    commissioned_at: commissionDate,
    link_rate_gbps: item.link_rate_gbps ?? item.link_rate ?? '',
    fiber_tenancy_type: tenancyLabel,
    tenancy_type: tenancyLabel,
    tenancy_type_normalized: normalizedTenancy,
    is_row: normalizedIsRow,
    scope_year: item.scope_year ?? item.scopeYear ?? '',
    lease_start_date: item.lease_start_date ?? null,
    lease_end_date: item.lease_end_date ?? null,
    concern_department: item.concern_department ?? 'N/A',
    poc: item.poc ?? 'N/A',
    poc_phone: item.poc_phone ?? 'N/A',
    vendor: item.vendor ?? '',
  };
};

const LeasedFiberMapComponent = () => {
  const mapContainer = useRef(null);
  const controllerRef = useRef(null);
  const markerRef = useRef(null);
  const popupRef = useRef(null);
  const pendingLinksFilterRef = useRef(null);
  const applyInFlightRef = useRef(false);
  const pendingApplyRef = useRef(null);
  const pendingMarkerRef = useRef(null);
  const [markerRequestId, setMarkerRequestId] = useState(0);
  const navigate = useNavigate();
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const map = useRef(null);
  const myOwnedFiber = useAtomValue(myFiber);
  const myLeasedFiber = useAtomValue(leasedFiber);
  const myRowFiber = useAtomValue(rowFiber);
  const currentKmzNetwork = useAtomValue(kmzNetwork);
  const [lng, setLng] = useState(69.3451);
  const [lat, setLat] = useState(30.3753);
  const stepRef = useRef(0);
  const [viewType, setViewType] = useState('graph');
  const [state, setState] = useState({ right: false });
  const [tabValue, setTabValue] = useState();
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [ring, setRing] = useState('');
  const [site, setSite] = useState('');
  const [link, setLink] = useState('');
  const [inventoryData, setInventoryData] = useState([]);
  const [filteredInventoryData, setFilteredInventoryData] = useState([]);

  const [initialRegions, setInitialRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [rings, setRings] = useState([]);
  const [sites, setSites] = useState([]);
  const [links, setLinks] = useState([]);
  const geoLinksCacheRef = useRef({});
  const inventoryCacheRef = useRef({});
  const currentNetworkRef = useRef(currentKmzNetwork);

  // Update ref when network changes
  useEffect(() => {
    currentNetworkRef.current = currentKmzNetwork;
  }, [currentKmzNetwork]);

  const removeLayer = useCallback(() => {
    const sourceId = 'networklines';
    const layerId = 'lines-layer-network';

    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    if (map.current?.getLayer(layerId)) {
      map.current.removeLayer(layerId);
    }
    if (map.current?.getSource(sourceId)) {
      map.current.removeSource(sourceId);
    }
  }, []);

  const buildUniqueValues = (list, selector) => {
    if (!Array.isArray(list) || !list.length) {
      return [];
    }

    const values = list
      .map((item) => selector(item))
      .filter((value) => value !== null && value !== undefined && value !== '');

    return [...new Set(values)];
  };

  const buildUniqueSites = (list) => {
    if (!Array.isArray(list) || !list.length) {
      return [];
    }

    const values = list.flatMap((item) => [item.source_site_code, item.sink_site_code]);

    return [
      ...new Set(values.filter((value) => value !== null && value !== undefined && value !== '')),
    ];
  };

  const buildUniqueLinks = (list) => buildUniqueValues(list, (item) => `${item.layer_name || ''}`);

  const [regions, setRegions] = useState([
    {
      id: 'north-polygon',
      regionName: 'North',
      data: northPolygonData,
      color: '#c5d5ef',
      center: [72.94996, 34.97499],
    },
    {
      id: 'centralA-polygon',
      regionName: 'Central A',
      data: centralAPolygonData,
      color: '#f1bdb6',
      center: [73.40944, 30.59002],
    },
    {
      id: 'centralB-polygon',
      regionName: 'Central B',
      data: centralBPolygonData,
      color: '#c4d8a5',
      center: [71.16216, 30.48678],
    },
    {
      id: 'south-polygon',
      regionName: 'South',
      data: southPolygonData,
      color: '#f8e1b9',
      center: [66.5, 27.48678],
    },
    {
      id: 'kashmir-polygon',
      regionName: 'Kashmir',
      data: kashmirPolygonData,
      color: '#f2c3fa',
      center: [73.781, 33.9259],
    },
    {
      id: 'pakistan',
      regionName: 'Pakistan',
      data: completePakistanPolygonData,
      color: '#5f8282',
      center: [72.94996, 34.97499],
    },
  ]);

  const [regionsMpbn, setRegionsMpbn] = useState([
    {
      id: 'north-polygon',
      regionName: 'North',
      alarmsCount: 0,
      color: '#35527e',
      center: [72.94996378341335, 34.974994348955335],
    },
    {
      id: 'centralA-polygon',
      regionName: 'Center A',
      alarmsCount: 0,
      color: '#893e2a',
      center: [73.40944846601698, 30.590024194213726],
    },
    {
      id: 'centralB-polygon',
      regionName: 'Center B',
      alarmsCount: 0,
      color: '#66773c',
      center: [71.16216343821162, 30.486783539931153],
    },
    {
      id: 'south-polygon',
      regionName: 'South',
      alarmsCount: 0,
      color: '#906b23',
      center: [66.5, 27.486783539931153],
    },
    {
      id: 'pakistan',
      regionName: 'Pakistan',
      alarmsCount: 0,
      data: pakistanPolygonData,
      color: '#446363',
      center: [72.94996378341335, 34.974994348955335],
    },
    {
      id: 'kashmir-polygon',
      regionName: 'Kashmir',
      alarmsCount: 0,
      data: kashmirPolygonData,
      color: '#446363',
      center: [73.781, 33.9259],
    },
  ]);

  useEffect(() => {
    const cacheKey = currentKmzNetwork?.toLowerCase?.() ?? 'default';

    // Immediately load cached data if available (for instant display)
    if (inventoryCacheRef.current[cacheKey]) {
      const cachedData = inventoryCacheRef.current[cacheKey];
      setInventoryData(cachedData);
      setFilteredInventoryData(cachedData);
    } else {
      // Clear data while fetching if not cached
      setInventoryData([]);
      setFilteredInventoryData([]);
    }

    const fetchData = async () => {
      // Check cache again (might have been set by another component)
      if (inventoryCacheRef.current[cacheKey]) {
        return; // Already cached and loaded above
      }

      // If not in cache, fetch from API
      try {
        const result = await fetchInventoryDataByNetwork(currentKmzNetwork);
        const actualData = Array.isArray(result?.response) ? result.response : [];
        const normalizedData = actualData.map(normalizeInventoryItem);

        // Cache the data
        inventoryCacheRef.current[cacheKey] = normalizedData;

        // Only update state if we're still on the same network
        if (currentNetworkRef.current === currentKmzNetwork) {
          setInventoryData(normalizedData);
          setFilteredInventoryData(normalizedData);
        }
      } catch (error) {
        console.error('Error fetching inventory data:', error);
      }
    };

    if (currentKmzNetwork) {
      fetchData();
    }
  }, [currentKmzNetwork]);

  useEffect(() => {
    if (!Array.isArray(inventoryData)) {
      return;
    }

    if (!region && !city && !ring && !site && !link) {
      const allRegions = buildUniqueValues(inventoryData, (i) => i.source_region ?? i.region);
      const allCities = buildUniqueValues(inventoryData, (i) => i.source_city ?? i.city);
      const allRings = buildUniqueValues(
        inventoryData,
        (i) => i.source_ring ?? i.ring_unique_name ?? ''
      );
      const allSites = buildUniqueSites(inventoryData);
      const allLinks = buildUniqueLinks(inventoryData).filter(Boolean);

      setInitialRegions(allRegions);
      setCities(allCities);
      setRings(allRings);
      setSites(allSites);
      setLinks(allLinks);
    }
  }, [inventoryData, region, city, ring, site, link]);

  useEffect(() => {
    if (!region) {
      return;
    }

    const filtered = inventoryData.filter((i) => i.source_region === region || i.region === region);

    setCities(buildUniqueValues(filtered, (i) => i.source_city ?? i.city));
    setRings(buildUniqueValues(filtered, (i) => i.source_ring ?? i.ring_unique_name ?? ''));
    setSites(buildUniqueSites(filtered));
    setLinks(buildUniqueLinks(filtered).filter(Boolean));
  }, [region]);

  useEffect(() => {
    if (!city) {
      return;
    }

    const entry = inventoryData.find((i) => i.source_city === city || i.city === city);
    if (entry && !region) {
      setRegion(entry.source_region ?? entry.region ?? '');
    }

    const filtered = inventoryData.filter((i) => i.source_city === city || i.city === city);

    setRings(buildUniqueValues(filtered, (i) => i.source_ring ?? i.ring_unique_name ?? ''));
    setSites(buildUniqueSites(filtered));
    setLinks(buildUniqueLinks(filtered).filter(Boolean));
  }, [city]);

  useEffect(() => {
    if (!region && !city) {
      return;
    }

    const filtered = inventoryData.filter((i) => {
      const matchesRegion = !region || i.source_region === region || i.region === region;
      const matchesCity = !city || i.source_city === city || i.city === city;
      return matchesRegion && matchesCity;
    });

    setRings(buildUniqueValues(filtered, (i) => i.source_ring ?? i.ring_unique_name ?? ''));
    setSites(buildUniqueSites(filtered));
    setLinks(buildUniqueLinks(filtered).filter(Boolean));
  }, [region, city]);

  useEffect(() => {
    if (!ring) {
      return;
    }

    const entry = inventoryData.find((i) => i.source_ring === ring || i.ring_unique_name === ring);

    if (entry) {
      if (!region) {
        setRegion(entry.source_region ?? entry.region ?? '');
      }
      if (!city) {
        setCity(entry.source_city ?? entry.city ?? '');
      }
    }
  }, [ring]);

  useEffect(() => {
    if (!site || !inventoryData.length) {
      return;
    }

    const siteCode = site.trim().toUpperCase();

    const matchedEntries = inventoryData.filter(
      (i) =>
        i.source_site_code?.trim().toUpperCase() === siteCode ||
        i.sink_site_code?.trim().toUpperCase() === siteCode
    );

    const firstEntry = matchedEntries[0];
    if (firstEntry) {
      if (!region) {
        setRegion(firstEntry.source_region ?? firstEntry.region ?? '');
      }
      if (!city) {
        setCity(firstEntry.source_city ?? firstEntry.city ?? '');
      }
      if (!ring) {
        setRing(firstEntry.source_ring ?? firstEntry.ring_unique_name ?? '');
      }
    }

    const layerNames = matchedEntries.map((i) => i.layer_name).filter(Boolean);
    setLinks([...new Set(layerNames)]);
  }, [site]);

  useEffect(() => {
    if (!link) {
      return;
    }

    const entry = inventoryData.find((i) => `${i.layer_name}` === link);

    if (entry) {
      if (!region) {
        setRegion(entry.source_region ?? entry.region ?? '');
      }
      if (!city) {
        setCity(entry.source_city ?? entry.city ?? '');
      }
      if (!ring) {
        setRing(entry.source_ring ?? entry.ring_unique_name ?? '');
      }
      if (!site) {
        setSite(entry.source_site_code ?? entry.sink_site_code ?? '');
      }
    }
  }, [link]);

  // Ensure map resizes when view type changes
  useEffect(() => {
    if (viewType === 'graph' && map.current) {
      // Small delay to ensure the container is properly sized
      setTimeout(() => {
        map.current.resize();
      }, 100);
    }
  }, [viewType]);

  useEffect(() => {
    let tab = currentKmzNetwork.toLowerCase();

    if (!map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/standard',
        center: [lng, lat],
        zoom: 2,
        attributionControl: false,
      });
      map.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
      map.current.on('style.load', () => {
        console.log('Style fully loaded, adding boundary...');
        boundaryLayer(map.current, regions, regionsMpbn, tab);
        updateLayer();
      });
    } else {
      map.current.setCenter([lng, lat]);
    }
  }, []);

  const handleTabSelection = (tabValue) => {
    setTabValue(tabValue);
    sessionStorage.setItem('selectedTab', tabValue);
    navigate('/fibers');
  };

  const updateLayer = useCallback(async () => {
    // Store the network at the start to validate later
    const networkAtStart = currentKmzNetwork;

    const fiberVisibility = {
      myOwnedFiber,
      myLeasedFiber,
      myRowFiber,
    };

    removeLayer();
    removeWmsLayer(map);
    removeWmsSitesLayer(map);

    if (!map.current) {
      return;
    }

    // Verify network hasn't changed
    if (currentNetworkRef.current !== networkAtStart) {
      console.log('Network changed during updateLayer, aborting');
      return;
    }

    // Get inventory data from cache or use current state
    const cacheKey = currentKmzNetwork?.toLowerCase?.() ?? 'default';
    const cachedInventoryData = inventoryCacheRef.current[cacheKey] || inventoryData;

    // For OTN and MPBN, allow proceeding even if inventoryData is empty
    // For other networks, require inventoryData
    const isOTNOrMPBN = currentKmzNetwork === 'OTN' || currentKmzNetwork === 'MPBN';
    if (!isOTNOrMPBN && (!Array.isArray(cachedInventoryData) || cachedInventoryData.length === 0)) {
      return;
    }

    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    let linksData = geoLinksCacheRef.current[cacheKey];

    if (!linksData) {
      const fetchController = new AbortController();
      controllerRef.current = fetchController;
      showBackdrop();
      try {
        linksData = await geoServerLinks(currentKmzNetwork, fetchController.signal);
        if (!linksData) {
          console.log('request aborted or empty links data');
          hideBackdrop();
          return;
        }
        // Verify network hasn't changed after fetch
        if (currentNetworkRef.current !== networkAtStart) {
          console.log('Network changed after links fetch, aborting');
          hideBackdrop();
          return;
        }
        geoLinksCacheRef.current[cacheKey] = linksData;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching geo links:', error);
        }
        hideBackdrop();
        return;
      } finally {
        controllerRef.current = null;
      }
    }

    // Verify network hasn't changed before adding layers
    if (currentNetworkRef.current !== networkAtStart) {
      console.log('Network changed before adding links layer, aborting');
      return;
    }

    await addWmsLayer(
      map,
      currentKmzNetwork,
      popupRef,
      stepRef,
      showBackdrop,
      hideBackdrop,
      cachedInventoryData,
      fiberVisibility,
      linksData
    );

    // Verify network hasn't changed before adding sites
    if (currentNetworkRef.current !== networkAtStart) {
      console.log('Network changed before adding sites layer, aborting');
      return;
    }

    const siteController = new AbortController();
    controllerRef.current = siteController;
    await addWmsSitessLayer(siteController);
  }, [
    currentKmzNetwork,
    hideBackdrop,
    inventoryData,
    myLeasedFiber,
    myOwnedFiber,
    myRowFiber,
    removeLayer,
    showBackdrop,
    // Note: We intentionally don't include inventoryCacheRef in dependencies
    // as it's a ref and we want to use the latest cached data
  ]);

  const addWmsSitessLayer = async (controller) => {
    let tab = currentKmzNetwork.toLowerCase();

    try {
      // Remove existing layers and sources if they exist
      if (map.current.getLayer('sitesLayers')) {
        map.current.removeLayer('sitesLayers');
      }
      if (map.current.getLayer('highlightedSites')) {
        map.current.removeLayer('highlightedSites');
      }
      if (map.current.getSource('sitesLayers')) {
        map.current.removeSource('sitesLayers');
      }

      // For OTN and MPBN, always show sites (buttons are hidden but data should be displayed)
      const isOTNOrMPBN = currentKmzNetwork === 'OTN' || currentKmzNetwork === 'MPBN';

      if (!isOTNOrMPBN && !myOwnedFiber && !myLeasedFiber && !myRowFiber) {
        console.log('Skipping sites layer because no fiber layers are enabled');
        return;
      }

      if (!isOTNOrMPBN && myRowFiber && !myLeasedFiber && !myOwnedFiber) {
        console.log('Skipping sites layer because only RoW fiber is enabled');
        return;
      }

      // Stop any existing animation
      if (window.haloAnimationFrame) {
        cancelAnimationFrame(window.haloAnimationFrame);
      }

      const cacheKey = `${currentKmzNetwork?.toLowerCase?.() ?? 'default'}::sites`;
      let data = geoLinksCacheRef.current[cacheKey];

      if (!data) {
        data = await geoServerSites(currentKmzNetwork, controller.signal);

        if (data) {
          geoLinksCacheRef.current[cacheKey] = data;
        }
      }

      if (!data) {
        console.log('request aborted by user');
        return;
      }

      map.current.addSource('sitesLayers', {
        type: 'geojson',
        data: data,
      });

      // Add your layers as before...
      map.current.addLayer({
        id: 'sitesLayers',
        type: 'circle',
        source: 'sitesLayers',
        paint: {
          // 'circle-radius': 3,
          'circle-color': '#4B0082',
          'circle-stroke-color': '#9370DB',
          'circle-stroke-width': 2.5 / 1.8,
          'circle-stroke-opacity': 0.4,
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 3, 15, 10],
        },
      });
      map.current.addLayer({
        id: 'highlightedSites',
        type: 'circle',
        source: 'sitesLayers',
        paint: {
          'circle-radius': 4,
          'circle-color': '#e813c1',
          'circle-stroke-color': '#e813c1',
          'circle-stroke-width': 2,
          'circle-stroke-opacity': 0.6,
          'circle-opacity': 1,
        },
        filter: ['==', 'highlighted', true],
      });
      let haloSize = 3;
      let opacity = 0.5;
      let expanding = true;
      let maxHaloSize = 6;
      let minHaloSize = 3;
      let maxOpacity = 0.7;
      let minOpacity = 0.3;

      const animateHalo = () => {
        // Check if layer still exists before animating
        if (!map.current.getLayer('highlightedSites')) {
          return;
        }

        if (expanding) {
          haloSize += 0.05;
          opacity += 0.02;
          if (haloSize >= maxHaloSize || opacity >= maxOpacity) {
            expanding = false;
          }
        } else {
          haloSize -= 0.05;
          opacity -= 0.02;
          if (haloSize <= minHaloSize || opacity <= minOpacity) {
            expanding = true;
          }
        }

        map.current.setPaintProperty('highlightedSites', 'circle-radius', haloSize);
        map.current.setPaintProperty('highlightedSites', 'circle-opacity', opacity);

        window.haloAnimationFrame = requestAnimationFrame(animateHalo);
      };

      window.haloAnimationFrame = requestAnimationFrame(animateHalo);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error adding WFS layer:', error);
      }
    }
  };

  const cancelRef = useRef(false);

  // Immediately remove layers when network changes to prevent showing old data
  useEffect(() => {
    if (!map.current) {
      return;
    }

    // Stop any running animations first
    if (window.haloAnimationFrame) {
      cancelAnimationFrame(window.haloAnimationFrame);
      window.haloAnimationFrame = null;
    }

    // Remove all layers and sources synchronously
    // Remove links layers
    if (map.current.getLayer('linksLayers')) {
      map.current.removeLayer('linksLayers');
    }
    if (map.current.getLayer('linksLayers-dashed')) {
      map.current.removeLayer('linksLayers-dashed');
    }
    if (map.current.getSource('linksLayers')) {
      map.current.removeSource('linksLayers');
    }

    // Remove sites layers
    if (map.current.getLayer('sitesLayers')) {
      map.current.removeLayer('sitesLayers');
    }
    if (map.current.getLayer('highlightedSites')) {
      map.current.removeLayer('highlightedSites');
    }
    if (map.current.getSource('sitesLayers')) {
      map.current.removeSource('sitesLayers');
    }
    if (map.current.getSource('fiberSitesLayer')) {
      if (map.current.getLayer('fiberSitesLayer')) {
        map.current.removeLayer('fiberSitesLayer');
      }
      map.current.removeSource('fiberSitesLayer');
    }

    // Remove other layers
    removeLayer();

    // Remove popups
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    // Abort any ongoing requests
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  }, [currentKmzNetwork, removeLayer]);

  useEffect(() => {
    updateLayer();
  }, [updateLayer]);

  useEffect(() => {
    cancelRef.current = false;

    const handleLayers = async () => {
      await updateLayer();

      if (cancelRef.current) {
        removeWmsLayer(map);
        removeWmsSitesLayer(map);
      }
    };

    handleLayers();

    return () => {
      cancelRef.current = true;
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    };
  }, [currentKmzNetwork, updateLayer]);

  // When inventoryData changes (after network change or fetch), sync filteredInventoryData
  useEffect(() => {
    if (Array.isArray(inventoryData)) {
      const cacheKey = currentKmzNetwork?.toLowerCase?.() ?? 'default';
      const cachedData = inventoryCacheRef.current[cacheKey];

      // Only sync if the inventoryData matches the cache for current network
      // This ensures we're updating with the correct network's data
      if (
        cachedData === inventoryData ||
        (cachedData && cachedData.length === inventoryData.length)
      ) {
        setFilteredInventoryData(inventoryData);
      }
    }
  }, [inventoryData, currentKmzNetwork]);

  useEffect(() => {
    // When network changes, reset filters and update filtered data from cache
    handleReset({ hideFilters: false });
  }, [currentKmzNetwork]);

  useEffect(() => {
    // For OTN and MPBN, allow proceeding even if inventoryData is empty
    const isOTNOrMPBN = currentKmzNetwork === 'OTN' || currentKmzNetwork === 'MPBN';
    const cacheKey = currentKmzNetwork?.toLowerCase?.() ?? 'default';
    const cachedInventoryData = inventoryCacheRef.current[cacheKey] || inventoryData;

    if (
      !map.current ||
      (!isOTNOrMPBN && (!Array.isArray(cachedInventoryData) || cachedInventoryData.length === 0))
    ) {
      return;
    }

    updateLayer();
  }, [inventoryData, updateLayer, currentKmzNetwork]);

  const visibleFilters = {
    FTTS: { region: true, city: true, ring: true, site: true, link: true },
    MPBN: { region: false, city: true, ring: false, site: true, link: true },
    OTN: { region: true, city: true, ring: false, site: true, link: true },
    IPRAN: { region: true, city: true, ring: false, site: true, link: true },
  };

  const currentFilters = visibleFilters[currentKmzNetwork] || {};

  const getLinksGeoJsonFromMap = useCallback(() => {
    if (!map.current) {
      return null;
    }
    const src = map.current.getSource?.('linksLayers');
    const data = src?._data ?? src?._options?.data ?? null;
    return data && data.type === 'FeatureCollection' ? data : null;
  }, []);

  const waitForLinksGeoJson = useCallback(
    ({ timeoutMs = 8000 } = {}) =>
      new Promise((resolve) => {
        if (!map.current) {
          resolve(null);
          return;
        }

        const existing = getLinksGeoJsonFromMap();
        if (existing?.features?.length) {
          resolve(existing);
          return;
        }

        const start = Date.now();
        const cleanup = (handler) => {
          if (!map.current) {
            return;
          }
          map.current.off('sourcedata', handler);
          map.current.off('idle', handler);
        };

        const handler = (e) => {
          if (e?.sourceId && e.sourceId !== 'linksLayers') {
            return;
          }

          const data = getLinksGeoJsonFromMap();
          if (data?.features?.length) {
            cleanup(handler);
            resolve(data);
            return;
          }

          if (Date.now() - start >= timeoutMs) {
            cleanup(handler);
            resolve(null);
          }
        };

        map.current.on('sourcedata', handler);
        map.current.on('idle', handler);

        setTimeout(() => {
          handler();
        }, timeoutMs + 50);
      }),
    [getLinksGeoJsonFromMap]
  );

  const normalizeMatchKey = useCallback((value) => {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim().toLowerCase();
  }, []);

  const findMatchingLinksFeature = useCallback(
    (linksGeo, record) => {
      if (!linksGeo?.features?.length || !record) {
        return null;
      }

      const candidates = [
        record.layer_name,
        record.trail_name,
        record.layer_alarm_id,
        record.layerAlarmId,
      ]
        .map(normalizeMatchKey)
        .filter(Boolean);

      if (!candidates.length) {
        return null;
      }

      const keysForFeature = (feature) => {
        const props = feature?.properties || {};
        return [
          props.layer_name,
          props.layerName,
          props.trail_name,
          props.trailName,
          props.layer_alarm_id,
          props.layerAlarmId,
        ]
          .map(normalizeMatchKey)
          .filter(Boolean);
      };

      for (const f of linksGeo.features) {
        const keys = keysForFeature(f);
        if (keys.length && candidates.some((c) => keys.includes(c)) && f?.geometry?.coordinates) {
          return f;
        }
      }

      return (
        linksGeo.features.find((f) => {
          const keys = keysForFeature(f);
          return keys.length && candidates.some((c) => keys.includes(c));
        }) ?? null
      );
    },
    [normalizeMatchKey]
  );

  const flyToAndMark = useCallback(
    ({ lng, lat, matchedRecord, matchedFeature }) => {
      if (!map.current) {
        console.warn('[LeasedFiber] Map not ready yet');
        return;
      }

      const run = () => {
        if (!map.current) {
          return;
        }
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
          console.warn('[LeasedFiber] Missing/invalid center point', {
            lng,
            lat,
            matchedRecord,
            matchedFeature,
          });
          return;
        }

        try {
          map.current.resize?.();
        } catch (e) {
          // ignore
        }

        map.current.flyTo({
          center: [lng, lat],
          zoom: 12,
          essential: true,
          duration: 2000,
        });

        if (markerRef.current) {
          markerRef.current.remove();
        }

        markerRef.current = new mapboxgl.Marker().setLngLat([lng, lat]).addTo(map.current);
        console.log('[LeasedFiber] Marker updated', { lng, lat });
      };

      if (map.current.isStyleLoaded?.()) {
        run();
        return;
      }

      map.current.once('style.load', run);
      map.current.once('idle', run);
    },
    [markerRef]
  );

  const fetchRingCenter = useCallback(
    async ({ region, city, ring, site, link }) => {
      const tab = String(currentKmzNetwork || '').toLowerCase();

      const endpointMap = {
        otn: {
          url: isNewOtnImplementation() ? '/snmpOTN/ringCenter' : '/otn/ringCenter',
          params: { region, city, ring, linkName: link, site },
        },
        ftts: {
          url: '/ftts/ringCenter',
          params: { region, city, ring, linkName: link, site },
        },
        mpbn: {
          url: '/mpbn/ringCenter',
          params: { city, ring, linkName: link, site },
        },
        ipran: {
          url: '/ipran/ringCenter',
          params: { region, city, ring, linkName: link, site },
        },
      };

      const entry = endpointMap[tab];
      if (!entry?.url) {
        return null;
      }

      const response = await apiClient.get(entry.url, { params: entry.params });
      const foundObj =
        tab === 'mpbn' || tab === 'ftts' ? response?.data?.[0] : response?.data?.response?.[0];

      const lng = Number(foundObj?.[0]);
      const lat = Number(foundObj?.[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
        return null;
      }
      return { lng, lat };
    },
    [currentKmzNetwork]
  );

  const ensureMapRendered = useCallback(async () => {
    const waitForVisible = async ({ timeoutMs = 2500 } = {}) => {
      const start = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const el = mapContainer.current;
        const ready = Boolean(el && el.clientWidth > 0 && el.clientHeight > 0);
        if (ready) {
          return true;
        }
        if (Date.now() - start >= timeoutMs) {
          return false;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => requestAnimationFrame(() => r()));
      }
    };

    await waitForVisible();
    await new Promise((r) => requestAnimationFrame(() => r()));
    try {
      map.current?.resize?.();
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (viewType !== 'graph') {
      return;
    }
    if (!pendingMarkerRef.current) {
      return;
    }
    const payload = pendingMarkerRef.current;
    pendingMarkerRef.current = null;
    flyToAndMark(payload);
  }, [flyToAndMark, viewType, markerRequestId]);

  const applyMarkerForRecord = useCallback(
    async ({ record, filteredCount, filtersSnapshot }) => {
      if (!filtersSnapshot) {
        return;
      }

      const { region, city, ring, site, link } = filtersSnapshot;
      const wantsPreciseCenter = Boolean(ring || site || link);

      let center = null;
      try {
        if (wantsPreciseCenter) {
          center = await fetchRingCenter({ region, city, ring, site, link });
        }
      } catch (e) {
        console.warn('[LeasedFiber] ringCenter lookup failed', e);
      }

      if (!center) {
        if (city) {
          const foundCity = findCityByAlias(all_cities, city);
          const lng = Number(foundCity?.lng);
          const lat = Number(foundCity?.lat);
          if (Number.isFinite(lng) && Number.isFinite(lat)) {
            center = { lng, lat };
          }
        } else if (region) {
          const foundRegion = findRegionByAlias(regions, region);
          const lng = Number(foundRegion?.center?.[0]);
          const lat = Number(foundRegion?.center?.[1]);
          if (Number.isFinite(lng) && Number.isFinite(lat)) {
            center = { lng, lat };
          }
        }
      }

      console.log('[LeasedFiber] Filter applied', {
        totalMatched: filteredCount,
        filters: filtersSnapshot,
        firstMatched: record,
        center,
      });

      if (!center) {
        console.warn('[LeasedFiber] No center resolved for marker', { filtersSnapshot });
        return;
      }

      pendingMarkerRef.current = {
        lng: center.lng,
        lat: center.lat,
        matchedRecord: record,
        matchedFeature: null,
      };
      setMarkerRequestId((v) => v + 1);

      const el = mapContainer.current;
      if (viewType === 'graph' && el && el.clientWidth > 0 && el.clientHeight > 0) {
        const payload = pendingMarkerRef.current;
        pendingMarkerRef.current = null;
        flyToAndMark(payload);
      }
    },
    [fetchRingCenter, flyToAndMark, regions, viewType]
  );

  // NOTE:
  // We intentionally do NOT filter/hide the link layers on "Apply".
  // Leased Fiber filtering should keep the base map visible and only navigate + mark the matched location.
  const applyLinksLayerFilter = useCallback((layerNamesLower) => {
    if (!map.current) {
      return;
    }

    const buildFilter = (names) => {
      if (!Array.isArray(names) || names.length === 0) {
        return null;
      }
      const layerOrTrailExpr = [
        'downcase',
        [
          'to-string',
          [
            'coalesce',
            ['get', 'layer_name'],
            ['get', 'layerName'],
            ['get', 'trail_name'],
            ['get', 'trailName'],
            '',
          ],
        ],
      ];
      return ['in', layerOrTrailExpr, ['literal', names]];
    };

    const filterExpr = buildFilter(layerNamesLower);

    const doApply = () => {
      if (
        !map.current?.getLayer?.('linksLayers') ||
        !map.current?.getLayer?.('linksLayers-dashed')
      ) {
        return false;
      }
      map.current.setFilter('linksLayers', filterExpr);
      map.current.setFilter('linksLayers-dashed', filterExpr);
      return true;
    };

    if (doApply()) {
      pendingLinksFilterRef.current = null;
      return;
    }

    pendingLinksFilterRef.current = filterExpr;
    map.current.once('idle', () => {
      if (!pendingLinksFilterRef.current) {
        return;
      }
      if (!map.current) {
        pendingLinksFilterRef.current = null;
        return;
      }
      if (map.current.getLayer('linksLayers')) {
        map.current.setFilter('linksLayers', pendingLinksFilterRef.current);
      }
      if (map.current.getLayer('linksLayers-dashed')) {
        map.current.setFilter('linksLayers-dashed', pendingLinksFilterRef.current);
      }
      pendingLinksFilterRef.current = null;
    });
  }, []);

  const handleReset = ({ hideFilters = true } = {}) => {
    setRegion('');
    setCity('');
    setRing('');
    setSite('');
    setLink('');

    // Get the correct inventory data for the current network from cache
    const cacheKey = currentKmzNetwork?.toLowerCase?.() ?? 'default';
    const currentNetworkInventoryData = inventoryCacheRef.current[cacheKey] || inventoryData;

    setFilteredInventoryData(currentNetworkInventoryData);

    const allRegions = buildUniqueValues(
      currentNetworkInventoryData,
      (i) => i.source_region ?? i.region
    );
    const allCities = buildUniqueValues(
      currentNetworkInventoryData,
      (i) => i.source_city ?? i.city
    );
    const allRings = buildUniqueValues(
      currentNetworkInventoryData,
      (i) => i.source_ring ?? i.ring_unique_name ?? ''
    );
    const allSites = buildUniqueSites(currentNetworkInventoryData);
    const allLinks = buildUniqueLinks(currentNetworkInventoryData).filter(Boolean);

    setInitialRegions(allRegions);
    setCities(allCities);
    setRings(allRings);
    setSites(allSites);
    setLinks(allLinks);

    handleRefresh(map, markerRef);
    // Keep base map layers visible; just clear marker/viewport.

    if (hideFilters) {
      setState({ right: false });
    }
  };

  const handleApply = () => {
    if (
      (!currentFilters.region || !region) &&
      (!currentFilters.city || !city) &&
      (!currentFilters.ring || !ring) &&
      (!currentFilters.site || !site) &&
      (!currentFilters.link || !link)
    ) {
      console.log('No active filters — skipping apply');
      return;
    }

    const filtered = inventoryData.filter((item) => {
      const matchesRegion =
        !currentFilters.region ||
        !region ||
        item.source_region === region ||
        item.region === region;
      const matchesCity =
        !currentFilters.city || !city || item.source_city === city || item.city === city;
      const matchesRing =
        !currentFilters.ring ||
        !ring ||
        item.source_ring === ring ||
        item.ring_unique_name === ring;
      const matchesSite =
        !currentFilters.site ||
        !site ||
        item.source_site_code === site ||
        item.sink_site_code === site;
      const matchesLink = !currentFilters.link || !link || `${item.layer_name}` === `${link}`;

      return matchesRegion && matchesCity && matchesRing && matchesSite && matchesLink;
    });

    setFilteredInventoryData(filtered);

    setViewType('graph');

    if (filtered.length > 0) {
      const first = filtered[0];
      const payload = {
        record: first,
        filteredCount: filtered.length,
        filtersSnapshot: { region, city, ring, site, link },
        ts: Date.now(),
      };
      pendingApplyRef.current = payload;

      if (applyInFlightRef.current) {
        return;
      }

      applyInFlightRef.current = true;

      (async () => {
        const startedTs = pendingApplyRef.current?.ts;
        await applyMarkerForRecord(pendingApplyRef.current);
        applyInFlightRef.current = false;

        const latestTs = pendingApplyRef.current?.ts;
        if (latestTs && startedTs && latestTs !== startedTs) {
          handleApply();
        }
      })();
    } else {
      console.log('[LeasedFiber] Filter applied — no matches', {
        filters: { region, city, ring, site, link },
      });
    }
  };

  const handleInventoryUpdate = useCallback(
    (updatedRecord, recordId) => {
      if (!updatedRecord || !recordId) {
        console.error('Invalid update data');
        return;
      }

      // Normalize the updated record
      const normalizedUpdatedRecord = normalizeInventoryItem(updatedRecord);

      // Helper function to extract ID from an item
      const getItemId = (item) => {
        return (
          item.id || item._id || item.link_id || item.inventory_id || item.record_id || item.uuid
        );
      };

      // Update the cache
      const cacheKey = currentKmzNetwork?.toLowerCase?.() ?? 'default';
      if (inventoryCacheRef.current[cacheKey]) {
        const cachedData = inventoryCacheRef.current[cacheKey];
        const updatedCache = cachedData.map((item) => {
          const itemId = getItemId(item);
          if (itemId && (itemId === recordId || String(itemId) === String(recordId))) {
            return normalizedUpdatedRecord;
          }
          return item;
        });
        inventoryCacheRef.current[cacheKey] = updatedCache;
      }

      // Update the inventoryData state
      setInventoryData((prevData) => {
        const updatedData = prevData.map((item) => {
          const itemId = getItemId(item);
          if (itemId && (itemId === recordId || String(itemId) === String(recordId))) {
            return normalizedUpdatedRecord;
          }
          return item;
        });
        return updatedData;
      });

      // Update filteredInventoryData if it exists
      setFilteredInventoryData((prevFiltered) => {
        if (!Array.isArray(prevFiltered) || prevFiltered.length === 0) {
          return prevFiltered;
        }
        const updatedFiltered = prevFiltered.map((item) => {
          const itemId = getItemId(item);
          if (itemId && (itemId === recordId || String(itemId) === String(recordId))) {
            return normalizedUpdatedRecord;
          }
          return item;
        });
        return updatedFiltered;
      });
    },
    [currentKmzNetwork]
  );

  return (
    <DashboardLayout
      handleReset={handleReset}
      handleDrawerClose={() => setState({ right: false })}
      state={state}
      list={() => (
        <LeasedFiberFilterDrawer
          selectedTab={currentKmzNetwork}
          regions={initialRegions}
          cities={cities}
          rings={rings}
          sites={sites}
          links={links}
          region={region}
          setRegion={setRegion}
          city={city}
          setCity={setCity}
          ring={ring}
          setRing={setRing}
          site={site}
          setSite={setSite}
          link={link}
          setLink={setLink}
          handleRegionChange={() => {}} // Not used in leased fiber but required by DashboardLayout
          handleCityChange={() => {}} // Not used in leased fiber but required by DashboardLayout
          handleSiteChange={() => {}} // Not used in leased fiber but required by DashboardLayout
          handleLinkChange={() => {}} // Not used in leased fiber but required by DashboardLayout
          handleReset={handleReset}
          handleApply={handleApply}
        />
      )}
      totalAlarms={filteredInventoryData}
      value={currentKmzNetwork}
      setValue={setTabValue}
      toggleDrawer={(anchor, open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
          return;
        }
        setState({ ...state, [anchor]: open });
      }}
      applyFilters={state.right}
      selectedTab={currentKmzNetwork}
      regions={initialRegions}
      cities={cities}
      rings={rings}
      sites={sites}
      links={links}
      region={region}
      setRegion={setRegion}
      city={city}
      setCity={setCity}
      ring={ring}
      setRing={setRing}
      site={site}
      setSite={setSite}
      link={link}
      setLink={setLink}
      handleRegionChange={() => {}} // Not used in leased fiber but required by DashboardLayout
      handleCityChange={() => {}} // Not used in leased fiber but required by DashboardLayout
      handleSiteChange={() => {}} // Not used in leased fiber but required by DashboardLayout
      handleLinkChange={() => {}} // Not used in leased fiber but required by DashboardLayout
      handleApply={handleApply}
      setFiltersValue={() => {}} // Not used in leased fiber but required by DashboardLayout
    >
      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden', // Prevent overflow issues with map
        }}
      >
        {/* Top-right buttons */}

        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            display: 'flex',
            // flexDirection: 'column',
            gap: '10px',
            zIndex: 1,
            width: '100%',
          }}
        >
          <div data-svg-wrapper style={{ marginLeft: '5px' }}>
            <LeasedFiberButtonsComponent />
          </div>
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 20,
              zIndex: 10,
              display: 'flex',
              gap: '10px',
            }}
          >
            <Tooltip title="Filter">
              <TuneIcon
                className={state.right ? 'table-tab-icons2' : 'table-tab-icon2'}
                onClick={() => setState({ right: !state.right })}
              />
            </Tooltip>

            <Box className="map-table-group">
              <Tooltip title="Map">
                <MapIcon
                  className={`map-table-btn ${viewType === 'graph' ? 'active' : ''}`}
                  onClick={() => setViewType('graph')}
                />
              </Tooltip>
              <Tooltip title="Table">
                <TableChartIcon
                  className={`map-table-btn ${viewType === 'table' ? 'active' : ''}`}
                  onClick={() => setViewType('table')}
                />
              </Tooltip>
            </Box>
          </Box>
        </div>

        {/* Map View */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            display: viewType === 'graph' ? 'block' : 'none',
            width: '100%',
            height: '100%',
          }}
        >
          <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
          <PartnerAndNetwrokButtons />
          <RefreshMap map={map} markerRef={markerRef} />
          {myLeasedFiber && !myOwnedFiber && !myRowFiber && (
            <Box
              sx={{
                position: 'absolute',
                top: 75,
                right: 20,
                zIndex: 10,
              }}
            >
              <VendorLegend network={currentKmzNetwork} />
            </Box>
          )}
        </div>

        {/* Table View */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            display: viewType === 'table' ? 'block' : 'none',
            width: '100%',
            height: '100%',
            overflow: 'auto',
            backgroundColor: '#fff',
          }}
        >
          <div style={{ padding: '5px' }}>
            <LeasedFiberTable
              data={filteredInventoryData}
              onUpdateRecord={handleInventoryUpdate}
              network={currentKmzNetwork}
            />
          </div>
        </div>
      </Box>
    </DashboardLayout>
  );
};

export default LeasedFiberMapComponent;
